import { EventIdentifier, TrackingEventType, useAnalytics, useIntl } from '@capsicum/common';
import { useRouter } from '@capsicum/common-web';
import { useWCTutorials } from '@capsicum/features/world-championship-pledge/shared';
import { worldChampionshipTutorialAtom } from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type SwiperClass, type SwiperProps } from 'swiper/react';

import { TutorialStep } from '../ui/tutorials/TutorialStep';

export const useWCPledgeTutorialStepper = (swiper: null | SwiperClass | undefined) => {
	const analytics = useAnalytics();
	const { onboardingTutorialSteps } = useWCTutorials();
	const { push } = useRouter();
	const setHasSeenTutorial = useSetAtom(worldChampionshipTutorialAtom);
	const swiperRef = useRef<null | SwiperClass>(swiper || null);
	const { $t } = useIntl();
	const [buttonText, setButtonText] = useState($t('global.button.continue'));

	useEffect(() => {
		swiperRef.current = swiper || null;
	}, [swiper]);

	const handleContinue = () => {
		setHasSeenTutorial(true);
		analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
			action: 'wc_pledge_tutorial_finished'
		});
		analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
			action: 'wc_pledge_tokens_view'
		});
		push('/locker-room-internationals');
	};

	const goToSlide = useCallback((index: number) => {
		swiperRef.current?.slideTo(index - 1);
	}, []);

	const steps = useMemo(
		() =>
			onboardingTutorialSteps.map((props, index) => {
				function TutorialStepComponent() {
					return (
						<TutorialStep
							{...props}
							goToStep={goToSlide}
							numberOfSteps={onboardingTutorialSteps.length}
							stepNumber={index + 1}
						/>
					);
				}

				return TutorialStepComponent;
			}),
		[onboardingTutorialSteps, goToSlide]
	);

	const buttonAction = useCallback(() => {
		swiper?.activeIndex === steps.length - 1 ? handleContinue() : swiper?.slideNext();
	}, [handleContinue]);

	const onSlideChange = useCallback<Exclude<SwiperProps['onSlideChange'], undefined>>(
		swiperInstance => {
			setButtonText(
				swiperInstance.activeIndex === steps.length - 1
					? $t('global.button.letsGetStarted')
					: $t('global.button.continue')
			);
		},
		[setButtonText]
	);

	return {
		buttonAction,
		buttonText,
		onSlideChange,
		steps
	};
};
